import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { Box, Link, Tooltip } from "@mui/material";
import { keyframes } from "@mui/system";
import React from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatDuration, toMs, useNow } from "./duration.js";

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

// A moving sheen on the bar's track while the task is running.
const RUNNING_TRACK_SX = {
    backgroundImage:
        "linear-gradient(90deg, rgba(0,0,0,0.04) 25%, rgba(0,0,0,0.12) 50%, rgba(0,0,0,0.04) 75%)",
    backgroundSize: "200% 100%",
    animation: `${shimmer} 1.6s linear infinite`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const numberFormat = new Intl.NumberFormat();
const SKIPPED_COLOR = "#E0A23B";
const RESTRICTED_COLOR = "#7B5EA7";
// Muted green: done before this run, so it reads as context, not progress.
const ALREADY_DONE_COLOR = "rgba(46, 125, 50, 0.35)";
const MAX_INLINE_NAMES = 3;

const DEFAULT_LABELS = Object.freeze({
    alreadyDone: { label: "already done" },
    succeeded: { label: "succeeded" },
    failed: { label: "failed" },
    skipped: { label: "skipped" },
    notEnoughSamples: { label: "not enough samples" },
    restricted: { label: "restricted" },
});

// Outcomes after "failed" that only appear when a task reports them.
const OPTIONAL_OUTCOMES = [
    { key: "notEnoughSamples", color: SKIPPED_COLOR },
    { key: "restricted", color: RESTRICTED_COLOR },
    { key: "skipped", color: SKIPPED_COLOR },
];

const formatUpdatedAt = (updatedAt) => {
    const time = updatedAt ? new Date(updatedAt) : null;
    return time && !Number.isNaN(time.getTime())
        ? `Updated ${time.toLocaleString()}`
        : null;
};

const LegendItem = ({ color, count, label, hint }) => (
    <Tooltip title={hint || ""} placement="bottom" arrow>
        <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
        >
            <Box
                component="span"
                aria-hidden
                sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }}
            />
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                {numberFormat.format(count)}
            </Box>
            {label}
        </Box>
    </Tooltip>
);

const Segment = ({ width, color, separated }) => (
    <Box
        sx={{
            width: `${width}%`,
            bgcolor: color,
            // A hairline keeps adjacent segments distinct.
            borderLeft: separated && width > 0 ? "2px solid white" : "none",
            transition: "width 400ms ease",
        }}
    />
);

const MissingItems = ({ names, noun, label }) => {
    const inline = names.slice(0, MAX_INLINE_NAMES);
    const hidden = names.length - inline.length;
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                mt: 0.25,
                px: 1,
                py: 0.75,
                borderRadius: "8px",
                bgcolor: "rgba(224, 162, 59, 0.12)",
                fontSize: 12,
                lineHeight: 1.45,
            }}
        >
            <PersonOffOutlinedIcon
                aria-hidden
                sx={{ fontSize: 16, color: "#9A6412", mt: "1px", flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0 }}>
                <Box component="span" sx={{ fontWeight: 600, color: "#6B4409" }}>
                    {`${numberFormat.format(names.length)} ${noun}${names.length === 1 ? "" : "s"} ${label}`}
                </Box>
                <Box
                    component="span"
                    sx={{ color: "text.secondary", overflowWrap: "anywhere" }}
                >
                    {`: ${inline.join(", ")}`}
                    {hidden > 0 && (
                        <Tooltip title={names.slice(MAX_INLINE_NAMES).join(", ")} arrow>
                            <Box
                                component="span"
                                tabIndex={0}
                                sx={{
                                    ml: 0.5,
                                    fontWeight: 600,
                                    color: "#6B4409",
                                    cursor: "help",
                                    textDecoration: "underline dotted",
                                }}
                            >
                                {`+${hidden} more`}
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// Where to inspect a task's results in detail, e.g. the project voices page.
// `onClick` runs before navigation, e.g. to select the project a page reads.
const DetailsLink = ({ to, label, onClick }) => (
    <Link
        component={RouterLink}
        to={to}
        onClick={onClick}
        underline="none"
        sx={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 12,
            fontWeight: 600,
            color: "var(--color-link-text)",
            borderRadius: "6px",
            "& .details-arrow": { transition: "transform 150ms ease" },
            "&:hover": { textDecoration: "underline" },
            "&:hover .details-arrow": { transform: "translateX(2px)" },
            "&:focus-visible": {
                outline: "2px solid var(--color-link-text)",
                outlineOffset: 2,
            },
        }}
    >
        {label}
        <ArrowForwardRoundedIcon className="details-arrow" aria-hidden sx={{ fontSize: 14 }} />
    </Link>
);

// One compact row per language when a batch spans several languages.
const LanguageBreakdown = ({ entries, languageName }) => (
    <Box
        component="ul"
        aria-label="Progress by language"
        sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            display: "grid",
            gridTemplateColumns: "minmax(64px, auto) 1fr auto",
            columnGap: 1,
            rowGap: 0.5,
            alignItems: "center",
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
        }}
    >
        {entries.map((entry) => {
            const done = entry.alreadyDone + entry.succeeded + entry.failed + entry.skipped;
            const share = (count) => (entry.total > 0 ? (count / entry.total) * 100 : 0);
            return (
                <Box component="li" key={entry.language} sx={{ display: "contents" }}>
                    <Box component="span" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                        {languageName(entry.language)}
                    </Box>
                    <Box
                        aria-hidden
                        sx={{
                            display: "flex",
                            height: 4,
                            borderRadius: 999,
                            overflow: "hidden",
                            bgcolor: "rgba(0, 0, 0, 0.08)",
                        }}
                    >
                        <Segment width={share(entry.alreadyDone)} color={ALREADY_DONE_COLOR} />
                        <Segment width={share(entry.succeeded)} color="success.main" />
                        <Segment width={share(entry.failed)} color="error.main" />
                        <Segment width={share(entry.skipped)} color={SKIPPED_COLOR} />
                    </Box>
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {numberFormat.format(Math.min(done, entry.total))}
                        </Box>
                        <Box component="span" sx={{ color: "text.secondary" }}>
                            {` / ${numberFormat.format(entry.total)}`}
                        </Box>
                        {entry.failed > 0 && (
                            <Box component="span" sx={{ color: "error.main", ml: 0.75 }}>
                                {`${numberFormat.format(entry.failed)} failed`}
                            </Box>
                        )}
                    </Box>
                </Box>
            );
        })}
    </Box>
);

// Outcome counts for tasks that process items one by one. The bar is
// segmented so failures and skips stay visible instead of disappearing into a
// percentage. `labels` renames outcomes per task; `missingNoun` names the
// entities listed in `progress.charactersWithoutVoices`. `detailsLink` is one
// link or a list of links shown under the progress.
const TaskProgressCell = ({
    progress,
    running = false,
    noun = "items",
    labels = DEFAULT_LABELS,
    missingNoun = "character",
    missingLabel = "with no voices",
    detailsLink = null,
    languageName = (language) => language,
}) => {
    const startedAtMs = toMs(progress?.startedAt);
    const now = useNow(Boolean(running && startedAtMs !== null));
    const links = [].concat(detailsLink || []);
    const linkList = links.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.5 }}>
            {links.map((link) => (
                <DetailsLink key={link.to} {...link} />
            ))}
        </Box>
    );

    // A run that has started but not reported yet, e.g. while a Cloud Run Job
    // starts up, which can take a couple of minutes.
    if (!progress && running) {
        return (
            <Box sx={{ minWidth: 200, display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box component="span" sx={{ color: "text.secondary", fontSize: 14 }}>
                    Starting…
                </Box>
                <Box
                    role="progressbar"
                    aria-label="Starting"
                    sx={{
                        height: 6,
                        borderRadius: 999,
                        bgcolor: "rgba(0, 0, 0, 0.08)",
                        ...RUNNING_TRACK_SX,
                    }}
                />
                {linkList}
            </Box>
        );
    }

    if (!progress) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box component="span" sx={{ color: "text.disabled" }}>
                    —
                </Box>
                {linkList}
            </Box>
        );
    }

    const { total, succeeded, failed, updatedAt } = progress;
    const optional = OPTIONAL_OUTCOMES.map((item) => ({
        ...item,
        count: progress[item.key] || 0,
    }));
    const reported = optional.filter((item) => item.count > 0);
    const optionalTotal = reported.reduce((sum, item) => sum + item.count, 0);
    const alreadyDone = progress.alreadyDone || 0;
    const missing = progress.charactersWithoutVoices || [];
    const byLanguage = (progress.byLanguage || []).map((entry) => ({
        alreadyDone: 0,
        succeeded: 0,
        failed: 0,
        skipped: 0,
        ...entry,
    }));
    const processed = Math.min(alreadyDone + succeeded + failed + optionalTotal, total);
    const share = (count) => (total > 0 ? (count / total) * 100 : 0);
    const percent = Math.round(share(processed));
    // Live while running; once finished, the last progress write marks the end.
    const endMs = running ? now : toMs(updatedAt);
    const elapsed =
        startedAtMs !== null && endMs !== null ? formatDuration(endMs - startedAtMs) : null;
    const outcome = (key) => ({ ...DEFAULT_LABELS[key], ...labels[key] });
    const summary = [
        `${numberFormat.format(processed)} of ${numberFormat.format(total)} ${noun} processed`,
        ...(alreadyDone
            ? [`${numberFormat.format(alreadyDone)} ${outcome("alreadyDone").label}`]
            : []),
        `${numberFormat.format(succeeded)} ${outcome("succeeded").label}`,
        `${numberFormat.format(failed)} ${outcome("failed").label}`,
        ...reported.map((item) => `${numberFormat.format(item.count)} ${outcome(item.key).label}`),
    ].join(", ");
    let segmentsBefore = alreadyDone + succeeded + failed;

    return (
        <Box sx={{ minWidth: 200, display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Tooltip title={formatUpdatedAt(updatedAt) || ""} placement="top" arrow>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            fontVariantNumeric: "tabular-nums",
                            fontSize: 14,
                        }}
                    >
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {numberFormat.format(processed)}
                            <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
                                {` / ${numberFormat.format(total)} ${noun}`}
                            </Box>
                        </Box>
                        <Box component="span" sx={{ color: "text.secondary", fontSize: 12 }}>
                            {percent}%
                            {elapsed && (
                                <Box
                                    component="span"
                                    title={running ? "Elapsed so far" : "Total time"}
                                >
                                    {` · ${elapsed}`}
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box
                        role="progressbar"
                        aria-label={summary}
                        aria-valuemin={0}
                        aria-valuemax={total}
                        aria-valuenow={processed}
                        sx={{
                            display: "flex",
                            height: 6,
                            borderRadius: 999,
                            overflow: "hidden",
                            bgcolor: "rgba(0, 0, 0, 0.08)",
                            ...(running && RUNNING_TRACK_SX),
                        }}
                    >
                        <Segment width={share(alreadyDone)} color={ALREADY_DONE_COLOR} />
                        <Segment
                            width={share(succeeded)}
                            color="success.main"
                            separated={alreadyDone > 0}
                        />
                        <Segment
                            width={share(failed)}
                            color="error.main"
                            separated={alreadyDone + succeeded > 0}
                        />
                        {reported.map((item) => {
                            const separated = segmentsBefore > 0;
                            segmentsBefore += item.count;
                            return (
                                <Segment
                                    key={item.key}
                                    width={share(item.count)}
                                    color={item.color}
                                    separated={separated}
                                />
                            );
                        })}
                    </Box>
                </Box>
            </Tooltip>

            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    columnGap: 1.5,
                    rowGap: 0.25,
                    fontSize: 12,
                    color: "text.secondary",
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {alreadyDone > 0 && (
                    <LegendItem
                        color={ALREADY_DONE_COLOR}
                        count={alreadyDone}
                        {...outcome("alreadyDone")}
                    />
                )}
                <LegendItem color="success.main" count={succeeded} {...outcome("succeeded")} />
                <LegendItem
                    color={failed > 0 ? "error.main" : "rgba(0, 0, 0, 0.2)"}
                    count={failed}
                    {...outcome("failed")}
                />
                {reported.map((item) => (
                    <LegendItem
                        key={item.key}
                        color={item.color}
                        count={item.count}
                        {...outcome(item.key)}
                    />
                ))}
            </Box>

            {byLanguage.length > 1 && (
                <LanguageBreakdown entries={byLanguage} languageName={languageName} />
            )}

            {missing.length > 0 && (
                <MissingItems names={missing} noun={missingNoun} label={missingLabel} />
            )}
            {linkList}
        </Box>
    );
};

export default TaskProgressCell;
